import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Skeleton, Theme } from '@mui/material';

import Switch from '../Switch';
import { AreaUnit } from '../../../shared/graphql/graphql';

export default function AreaUnitToggle({
  showPlaceholder,
  areaUnit,
  onAreaUnitChange,
}: {
  showPlaceholder?: boolean;
  areaUnit: AreaUnit;
  onAreaUnitChange: (unit: AreaUnit) => void;
}) {
  const { t } = useTranslation();

  const handleValueChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    const selectedUnit = checked ? AreaUnit.Hectares : AreaUnit.Acres;
    onAreaUnitChange(selectedUnit);
  };

  const getButtonStyle = (theme: Theme, isSelected: boolean) => ({
    textTransform: 'capitalize',
    fontSize: { sx: '16px', sm: '28px' },
    lineHeight: { sx: '28px', sm: '36px' },
    p: '8px 12px',
    '&:focus': {
      outline: 'none',
      backgroundColor: 'white',
    },
    '&:hover': {
      backgroundColor: 'white',
    },
    ...(isSelected
      ? { color: theme.palette.primary.main, fontWeight: 500 }
      : { color: theme.palette.secondary.main, fontWeight: 400 }),
  });

  if (showPlaceholder) {
    return (
      <Skeleton
        variant="rounded"
        sx={{
          width: { xs: 190, sm: 280 },
          height: { xs: 40, sm: 50 },
          mt: { xs: 2, sm: 0 },
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        mt: { xs: 2, sm: 0 },
      }}
    >
      <Button
        variant="text"
        disableRipple
        sx={(theme) => ({
          justifyContent: 'right',
          ...getButtonStyle(theme, areaUnit === AreaUnit.Acres),
        })}
        onClick={() => onAreaUnitChange(AreaUnit.Acres)}
      >
        {t('area-units.plural.acres')}
      </Button>
      <Switch
        checked={areaUnit === AreaUnit.Hectares}
        onChange={handleValueChange}
      />
      <Button
        variant="text"
        disableRipple
        sx={(theme) => ({
          justifyContent: 'left',
          ...getButtonStyle(theme, areaUnit === AreaUnit.Hectares),
        })}
        onClick={() => onAreaUnitChange(AreaUnit.Hectares)}
      >
        {t('area-units.plural.hectares')}
      </Button>
    </Box>
  );
}
